import { useState, useEffect } from "react";
import _ from "lodash";
import Overview from "@/components/product-details/Overview";
import ItemsDetails from "@/components/product-details/ItemsDetails";
import Breadcrumbs from "@/components/breadcrumbs";
import Card from "@/components/product-card/large/Card";
import CartSidebarWrapper from "@/components/cart/sidebarWrapper";
import { structureBreadcrumbs } from "@/utils/helpers/structure-breadcrumbs.helpers";
import FourOFour from "@/components/error/FourOFour";
import en from "@/localization/en.json";
import MetaData from "@/components/seo/MetaData";
import { useFetchSingleProduct } from "repo/product";
import { useRouter } from "next/router";
import Loader from "@/components/loader";
import { fetchHomePageData } from "@/api/home";
import { getSafeStaticProps, orNull } from "@/utils/helpers/isr.helpers";
import AddToCartModal from "@/components/modal/AddToCartModal";
import * as fbq from "lib/fpixel";
import * as gtm from "lib/gtm";


function ProductDetails(prop) {
  const MAX_SIMILAR_PRODUCT_TO_SHOW = 4;

  const [product, setProduct] = useState(prop?.product);
  const [relatedProducts, setRelatedProducts] = useState(prop?.relatedProducts);
  const [metaData, setMetaData] = useState(prop?.metaData);
  const [schema, setSchema] = useState(prop?.schema);
  const [promotionalData, setPromotionalData] = useState(prop?.promotionalData);
  const router = useRouter();

  useEffect(() => {
    setProduct(prop?.product);
  }, [prop?.product]);

  const { refetch } = useFetchSingleProduct(router.query.slug, {
    onSuccess: ({
      product,
      relatedProducts,
      metaData,
      schema,
      promotionalData,
    }) => {
      setProduct(product);
      setRelatedProducts(relatedProducts);
      setMetaData(metaData);
      setSchema(schema);
      setPromotionalData(promotionalData);
    },
    enabled: false,
    retry: false,
  });

  useEffect(() => {
    if (_.isEmpty(router.query.slug)) return;
    fbq.viewItemEvent(product)
    gtm.viewItemEvent(product)
    refetch();
  }, [router.query.slug, refetch]);

  const productBreadcrumb = (path, productName) => {
    if (!path || !productName) return;
    let data = structureBreadcrumbs(path);

    return [...data, { value: productName, link: productName }];
  };

  const handleWishList = (id) => {
    let updateWishlistData = product.map((product) =>
      product.id === id
        ? { ...product, isWishList: !product.isWishList }
        : product
    );
    setProduct(updateWishlistData);
  };

  if (!router.query.slug) {
    return (
      <div className="h-[31.25rem]">
        <Loader />
      </div>
    );
  } else if (product?.status === false) {
    return (
      <>
        <MetaData {...metaData} schema={schema} />
        <FourOFour />
      </>
    );
  }

  if (_.isEmpty(product)) {
    return <FourOFour />;
  }

  return (
    <>
      <MetaData {...metaData} schema={schema} />

      <div className="px-[1.25rem] lg:px-[7.5rem] pt-[1rem]">
        <Breadcrumbs
          isCategoryPage
          breadcrumbs={productBreadcrumb(
            product?.categories[0]?.full_path,
            product?.name
          )}
        />
        <div className="mb-[4.375rem]">
          <Overview product={product} promotionalBannerInfo={promotionalData} />
        </div>

        <ItemsDetails product={product} />

        {relatedProducts.length > 0 && (
          <div className="mb-[5.625rem]">
            <h2 className="text-[1.75rem] mb-[2.5rem] font-medium text-[#0A101A]">
              {en["product.similar_products"]}
            </h2>
            <div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-[1.5rem]">
                {relatedProducts
                  ?.slice(0, MAX_SIMILAR_PRODUCT_TO_SHOW)
                  .map((product) => (
                    <Card
                      product={product}
                      key={product.slug}
                      handleWishList={() => handleWishList(product.id)}
                    />
                  ))}
              </div>
            </div>
          </div>
        )}

        <CartSidebarWrapper />
      </div>
      <AddToCartModal />
    </>
  );
}

export async function getStaticPaths() {
  const homeData = await fetchHomePageData();
  const {
    popular_products: popularProducts = [],
    deal_products: dealProducts = [],
    category_products: categories = [],
  } = homeData?.data ?? {};
  const categoryProducts = _.map(categories, "products");

  const hotProducts = [
    ...popularProducts,
    ...dealProducts,
    ...categoryProducts.flat(),
  ];
  // If the home API fails, pages are generated on first request (blocking).
  const paths = _.uniq(_.compact(_.map(hotProducts, "slug"))).map((slug) => ({
    params: { slug },
  }));
  return { paths, fallback: "blocking" };
}

export async function getStaticProps({ params }) {
  return getSafeStaticProps({
    url: `/v1/products/${params.slug}`,
    mapToProps: ({ data }) => {
      if (!data?.product) return null;
      return {
        product: data.product,
        relatedProducts: data.related_products ?? [],
        metaData: orNull(data.metadata),
        schema: orNull(data.schema),
        promotionalData: orNull(data.promotional_banner_info),
      };
    },
    // Only used if backend fails during `next build`; client refetch fills it.
    fallbackProps: { product: null, relatedProducts: [] },
  });
}

export default ProductDetails;
